import styled from '@emotion/styled/macro';
import React, { FC } from 'react';
import { rgba } from 'polished';
import { Skeleton, Flex, FlexProps, useTheme } from '@chakra-ui/react';

interface OverlayProps {
  overlayHeight?: string;
  type?: OverlayType;
}

const OverlayWrapper = styled(Flex)<{ overlayHeight: string }>`
  height: ${(props): string => props.overlayHeight};
  width: 100%;
  flex-direction: column;
`;

const Overlay: FC<FlexProps & OverlayProps> = ({ overlayHeight = '100px', type = 'standard', ...props }) => {
  const { colors } = useTheme();
  let OverlayBody;

  switch (type) {
    case 'graph':
      OverlayBody = (
        <>
          <Skeleton w="100%" mb="5%" h="4%" startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
          <Skeleton w="100%" mb="12%" h="4%" startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
          <Skeleton w="100%" h="75%" startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
        </>
      );
      break;
    case 'splitted':
      OverlayBody = (
        <>
          <Skeleton w="100%" mb="5%" h="5%" startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
          <Skeleton w="100%" mb="7%" h="5%" startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
          <Flex h="80%" w="100%">
            <Skeleton w="48%" h="100%" mr="4%" startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
            <Skeleton w="48%" h="100%" startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
          </Flex>
        </>
      );
      break;
    case 'header':
      OverlayBody = (
        <Flex h="100%" w="100%" alignItems="center">
          <Skeleton w="15%" h="50%" mr="5%" startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
          <Skeleton w="30%" h="20%" mr="30%" startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
          <Skeleton w="20%" h="20%" startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
        </Flex>
      );
      break;
    case 'list':
      OverlayBody = (
        <>
          <Skeleton w="30%" mb="60px" flex={1.5} startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
          <Skeleton w="100%" mb="25px" flex={1} startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
          <Skeleton w="100%" mb="25px" flex={1} startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
          <Skeleton w="100%" mb="25px" flex={1} startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
          <Skeleton w="100%" flex={1} startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
        </>
      );
      break;
    case 'sdg':
      OverlayBody = (
        <Flex h="100%">
          <Skeleton h="100%" mr="11%" w="26%" startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
          <Skeleton h="100%" mr="11%" w="26%" startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
          <Skeleton h="100%" w="26%" startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
        </Flex>
      );
      break;
    case 'input':
      OverlayBody = <Skeleton h="65px" mr="11%" w="250px" startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />;
      break;
    default:
      OverlayBody = (
        <>
          <Skeleton w="100%" mb="11%" h="26%" startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
          <Skeleton w="100%" mb="11%" h="26%" startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
          <Skeleton w="100%" h="26%" startColor={rgba(colors.gray[400], 0.5)} endColor={colors.gray[100]} />
        </>
      );
  }

  return (
    <OverlayWrapper overlayHeight={overlayHeight} data-testid="skeleton-overlay" {...props}>
      {OverlayBody}
    </OverlayWrapper>
  );
};

export default Overlay;
